import React, { useMemo, useRef, useCallback, useState } from "react";

const DEFAULT_COLORS = [
  "#3E97FF",
  "#F1416C",
  "#50CD89",
  "#FFC700",
  "#7239EA",
  "#009EF7",
  "#F8285A",
  "#17C653",
  "#FF6D3B",
  "#A855F7",
  "#14B8A6",
  "#F59E0B",
  "#EF4444",
  "#22C55E",
  "#3B82F6",
  "#EC4899",
  "#8B5CF6",
  "#06B6D4",
  "#84CC16",
  "#F97316",
  "#6366F1",
  "#E11D48",
  "#0EA5E9",
  "#65A30D",
  "#DC2626",
];

type SeriesItem = { name: string; data: number[] };

function fmtPct(ratio: number): string {
  const pct = ratio * 100;
  if (pct === 0) return "0%";
  if (pct >= 10) return `${Math.round(pct)}%`;
  if (pct >= 1) return `${pct.toFixed(1)}%`;
  return `${pct.toFixed(2)}%`;
}

function buildArc(
  cx: number,
  cy: number,
  oR: number,
  iR: number,
  a1: number,
  a2: number,
) {
  const cos = Math.cos,
    sin = Math.sin,
    large = a2 - a1 > Math.PI ? 1 : 0;
  return [
    `M${cx + cos(a1) * oR} ${cy + sin(a1) * oR}`,
    `A${oR} ${oR} 0 ${large} 1 ${cx + cos(a2) * oR} ${cy + sin(a2) * oR}`,
    `L${cx + cos(a2) * iR} ${cy + sin(a2) * iR}`,
    `A${iR} ${iR} 0 ${large} 0 ${cx + cos(a1) * iR} ${cy + sin(a1) * iR}Z`,
  ].join(" ");
}

let _uidCounter = 0;
function useUid(): string {
  const ref = useRef<string | null>(null);
  if (ref.current === null) ref.current = `pc${++_uidCounter}`;
  return ref.current;
}

// ─── Public component ─────────────────────────────────────────────────────────

const PieChart = ({
  className,
  style,
  data,
  categories,
  colors,
  height = 350,
  labelHoverFormatter,
  title,
  subtitle,
  withAnimation = true,
}: {
  className?: string;
  style?: React.CSSProperties;
  data?: number[] | SeriesItem[];
  categories?: string[];
  colors?: string[];
  height?: number;
  labelHoverFormatter?: (value: string) => string;
  title?: string;
  subtitle?: string;
  withAnimation?: boolean;
}) => (
  <div
    className={`rounded-card bg-surface shadow-card ${className ?? ""}`}
    style={style}>
    {(title || subtitle) && (
      <div className="px-5 pt-5">
        <h3 className="flex flex-col items-start">
          <span className="mb-1 text-xl font-bold text-gray-900">{title}</span>
          <span className="text-sm font-semibold text-muted">{subtitle}</span>
        </h3>
      </div>
    )}
    <div className="px-4 pb-4 pt-2">
      <SVGDonutChart
        data={data}
        categories={categories}
        colors={colors}
        height={height}
        labelHoverFormatter={labelHoverFormatter}
        withAnimation={withAnimation}
      />
    </div>
  </div>
);

export default PieChart;

// ─── SVG Donut ────────────────────────────────────────────────────────────────

function SVGDonutChart({
  data,
  categories,
  colors,
  height,
  labelHoverFormatter,
  withAnimation,
}: {
  data?: number[] | SeriesItem[];
  categories?: string[];
  colors?: string[];
  height: number;
  labelHoverFormatter?: (value: string) => string;
  withAnimation: boolean;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const uid = useUid();

  const enter = useCallback((i: number) => {
    if (clearTimer.current) clearTimeout(clearTimer.current);
    setHovered(i);

    // Scroll the hovered legend row into view when triggered from the chart
    if (legendRef.current) {
      const row = legendRef.current.children[i] as HTMLElement | undefined;
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, []);

  const leave = useCallback(() => {
    clearTimer.current = setTimeout(() => setHovered(null), 80);
  }, []);

  const values: number[] = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (typeof data[0] === "number") return data as number[];
    return (data as SeriesItem[]).map((s) => s.data[0] ?? 0);
  }, [data]);

  const cats = categories ?? values.map((_, i) => `Series ${i + 1}`);
  const palette = colors?.length ? colors : DEFAULT_COLORS;
  const total = values.reduce((a, b) => a + b, 0);

  const VB = 200,
    cx = VB / 2,
    cy = VB / 2,
    oR = 92,
    iR = 60;

  const slices = useMemo(() => {
    let angle = -Math.PI / 2;
    return values.map((val, i) => {
      const ratio = total > 0 ? val / total : 1 / Math.max(values.length, 1);
      const start = angle;
      const end = angle + ratio * Math.PI * 2;
      angle = end;
      return { val, ratio, start, end, color: palette[i % palette.length] };
    });
  }, [values, total, palette]);

  const hoveredSlice = hovered !== null ? slices[hovered] : null;

  return (
    <>
      <style>{`
        ._ds_slice {
          cursor: pointer;
          transition: opacity 0.15s ease;
          opacity: 1;
        }
        ._ds_slice._ds_active { opacity: 0.65 !important; }
        ._ds_slice._ds_dim { opacity: 0.2 !important; }

        @keyframes _ds_slice_in_${uid} {
          from { opacity: 0; transform: scale(0.6); }
          to   { opacity: 1; transform: scale(1); }
        }
        .slice-anim-${uid} {
          animation: _ds_slice_in_${uid} 0.4s cubic-bezier(0.34, 1.3, 0.64, 1) forwards;
        }

        @keyframes _ds_fadeup {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        ._ds_row {
          display: flex; align-items: center; gap: 10px;
          padding: 3px 0; margin: 1px 0; cursor: pointer;
          border-radius: 4px;
          transition: opacity 0.15s, background 0.12s;
        }
        ._ds_row:hover { background: #F5F8FF; }
        ._ds_row._ds_dim { opacity: 0.4; }
        ._ds_row._ds_highlighted { background: #F5F8FF; }
        ._ds_row._ds_anim {
          opacity: 0;
          animation: _ds_fadeup 0.35s ease forwards;
        }
      `}</style>

      <div style={{ display: "flex", width: "100%", alignItems: "stretch" }}>
        {/* ── 60% donut ── */}
        <div
          style={{
            width: "60%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px 0",
          }}>
          <svg
            viewBox={`0 0 ${VB} ${VB}`}
            style={{
              width: "100%",
              maxWidth: height,
              height: "auto",
              display: "block",
              overflow: "visible",
            }}>
            {slices.map((slice, i) => (
              <path
                key={i}
                d={buildArc(cx, cy, oR, iR, slice.start, slice.end)}
                fill={slice.color}
                stroke="white"
                strokeWidth={1.5}
                className={[
                  "_ds_slice",
                  withAnimation ? `slice-anim-${uid}` : "",
                  hovered === i ? "_ds_active" : "",
                  hovered !== null && hovered !== i ? "_ds_dim" : "",
                ].join(" ")}
                style={
                  withAnimation ? { animationDelay: `${i * 25}ms` } : undefined
                }
                onMouseEnter={() => enter(i)}
                onMouseLeave={leave}
              />
            ))}

            {/* Center text */}
            <text
              x={cx}
              y={cy - 8}
              textAnchor="middle"
              fontSize={hoveredSlice ? 14 : 18}
              fontWeight="700"
              fill="#181C32"
              style={{ pointerEvents: "none" }}>
              {hoveredSlice
                ? labelHoverFormatter
                  ? labelHoverFormatter(hoveredSlice.val.toString())
                  : hoveredSlice.val.toLocaleString()
                : total.toLocaleString()}
            </text>
            <text
              x={cx}
              y={cy + 11}
              textAnchor="middle"
              fontSize={9}
              fill="#A1A5B7"
              style={{ pointerEvents: "none" }}>
              {hoveredSlice ? fmtPct(hoveredSlice.ratio) : "Total"}
            </text>
          </svg>
        </div>

        {/* ── 40% legend ── */}
        <div
          ref={legendRef}
          style={{
            width: "40%",
            maxHeight: height,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "#E4E6EF transparent",
            alignSelf: "flex-start",
            paddingTop: 4,
          }}>
          {slices.map((slice, i) => {
            const isH = hovered === i;
            return (
              <div
                key={i}
                className={[
                  "_ds_row",
                  hovered !== null && !isH ? "_ds_dim" : "",
                  isH ? "_ds_highlighted" : "",
                  withAnimation ? "_ds_anim" : "",
                ].join(" ")}
                style={
                  withAnimation ? { animationDelay: `${i * 30}ms` } : undefined
                }
                onMouseEnter={() => enter(i)}
                onMouseLeave={leave}>
                <span
                  style={{
                    flexShrink: 0,
                    width: 11,
                    height: 11,
                    borderRadius: 3,
                    background: slice.color,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    color: "#181C32",
                    fontWeight: isH ? 600 : 400,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  {cats[i] ?? `Series ${i + 1}`}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    minWidth: 40,
                    textAlign: "right",
                    fontSize: 13,
                    fontWeight: 600,
                    color: isH ? "#181C32" : "#A1A5B7",
                  }}>
                  {fmtPct(slice.ratio)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
